import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Package, Loader2, ShoppingBag } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ShopOrderSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('loading');
  const [order, setOrder] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) { setStatus('error'); return; }
    pollStatus();
  }, [sessionId]);

  const pollStatus = async (attempt = 0) => {
    if (attempt >= 6) { setStatus('timeout'); return; }
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/shop/checkout/status/${sessionId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await res.json();
      if (data.payment_status === 'paid') {
        setOrder(data.order);
        setStatus('success');
        localStorage.removeItem('shop_cart');
        return;
      }
      if (data.status === 'expired') { setStatus('expired'); return; }
      setAttempts(attempt + 1);
      setTimeout(() => pollStatus(attempt + 1), 2000);
    } catch {
      setTimeout(() => pollStatus(attempt + 1), 2000);
    }
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-pink-500 mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Confirming your payment...</p>
          <p className="text-gray-400 text-sm mt-1">This may take a moment</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto px-4 text-center">
          <div className="bg-white rounded-3xl p-8 shadow-sm">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-500" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Order Confirmed!</h1>
            <p className="text-gray-600 mb-6">Thank you for your purchase. You'll receive a confirmation email shortly.</p>

            {order && (
              <div className="bg-gray-50 rounded-2xl p-4 mb-6 text-left">
                <h3 className="font-semibold text-gray-900 mb-3 text-sm">Order Summary</h3>
                {order.items?.map((item, i) => (
                  <div key={i} className="flex justify-between text-sm text-gray-600 mb-1">
                    <span>{item.name} × {item.quantity}</span>
                    <span>{order.currency_symbol || 'CA$'}{parseFloat(item.line_total).toFixed(2)}</span>
                  </div>
                ))}
                <div className="border-t border-gray-200 mt-2 pt-2 flex justify-between font-bold text-gray-900 text-sm">
                  <span>Total</span>
                  <span>{order.currency_symbol || 'CA$'}{parseFloat(order.total).toFixed(2)}</span>
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <Link to="/shop" className="flex-1 border border-gray-200 text-gray-700 font-semibold py-3 rounded-xl hover:bg-gray-50 transition-colors flex items-center justify-center gap-2">
                <ShoppingBag className="w-4 h-4" /> Continue Shopping
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="max-w-md mx-auto px-4 text-center">
        <div className="bg-white rounded-3xl p-8 shadow-sm">
          <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-700 mb-2">
            {status === 'expired' ? 'Session Expired' : 'Payment Pending'}
          </h1>
          <p className="text-gray-500 mb-6">
            {status === 'expired'
              ? 'Your payment session has expired. Please try again.'
              : 'We could not confirm your payment. Please check your email or try again.'}
          </p>
          <Link to="/shop/cart" className="bg-pink-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-pink-600 transition-colors inline-block">
            Back to Cart
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ShopOrderSuccessPage;
